import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../lib/adminApi";
import { PageHead, TInput, TArea, Sel, Toggle, Btn, Card, MediaPicker, Badge, statusTone } from "../../components/admin/ui";

const TYPES = ["Music Video", "Visualizer", "Album Film", "Trailer", "Mood Film", "Artwork Feature"];
const PUB = ["draft", "scheduled", "published", "archived"];

export default function AdminVisuals() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = () => adm.list("visuals").then(setItems).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const set = (k, v) => setEditing((x) => ({ ...x, [k]: v }));

  const save = async () => {
    try {
      const { _id, id, source, createdAt, updatedAt, ...body } = editing;
      if (id) await adm.update("visuals", id, body);
      else await adm.create("visuals", body);
      toast.success("Visual saved.");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const destroy = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try { await adm.remove("visuals", v.id); refresh(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-visuals">
      <PageHead title="Visuals" sub="Music videos, visualizers, films and trailers.">
        <Btn onClick={() => setEditing({ title: "", type: "Visualizer", date: String(new Date().getFullYear()), thumbnail: "", videoUrl: null, externalUrl: null, description: "", relatedRelease: "", featured: false, publicationStatus: "draft" })} testid="visual-new">NEW VISUAL</Btn>
      </PageHead>

      {editing && (
        <Card className="mb-8 grid md:grid-cols-3 gap-4">
          <TInput label="TITLE" value={editing.title} onChange={(v) => set("title", v)} testid="visual-title" />
          <Sel label="TYPE" value={editing.type} onChange={(v) => set("type", v)} options={TYPES.map((t) => ({ v: t, l: t }))} />
          <TInput label="DATE / YEAR" value={editing.date} onChange={(v) => set("date", v)} />
          <MediaPicker label="THUMBNAIL" value={editing.thumbnail} onChange={(v) => set("thumbnail", v)} mode="url" filterType="image" />
          <TInput label="VIDEO URL / EMBED (YouTube, Vimeo or file)" value={editing.videoUrl} onChange={(v) => set("videoUrl", v || null)} testid="visual-video-url" />
          <TInput label="EXTERNAL VIEWING LINK (OPTIONAL)" value={editing.externalUrl} onChange={(v) => set("externalUrl", v || null)} />
          <TInput label="RELATED RELEASE SLUG" value={editing.relatedRelease} onChange={(v) => set("relatedRelease", v)} />
          <Sel label="PUBLICATION" value={editing.publicationStatus} onChange={(v) => set("publicationStatus", v)} options={PUB.map((v) => ({ v, l: v.toUpperCase() }))} />
          <div className="flex items-end pb-1"><Toggle label="FEATURED" checked={!!editing.featured} onChange={(v) => set("featured", v)} /></div>
          <TArea label="DESCRIPTION" value={editing.description} onChange={(v) => set("description", v)} className="md:col-span-3" rows={2} />
          <div className="flex gap-3 md:col-span-3">
            <Btn onClick={save} testid="visual-save">SAVE</Btn>
            <Btn variant="ghost" onClick={() => setEditing(null)}>CANCEL</Btn>
          </div>
        </Card>
      )}

      {items.map((v) => (
        <div key={v.id} className="flex items-center justify-between gap-4 border-t border-white/10 py-4 px-2" data-testid={`visual-row-${v.id}`}>
          <div className="flex items-center gap-4 min-w-0">
            {v.thumbnail && <img src={v.thumbnail} alt="" className="w-14 h-9 object-cover" />}
            <div className="min-w-0">
              <p className="text-white/90 truncate">{v.title}</p>
              <p className="font-mono text-[9px] text-white/40 mt-0.5">{v.type?.toUpperCase()} · {v.date} · {v.videoUrl ? "FILM SUPPLIED" : "NO FILM YET"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {v.featured && <Badge tone="gold">FEATURED</Badge>}
            <Badge tone={statusTone(v.publicationStatus)}>{v.publicationStatus}</Badge>
            <Btn variant="ghost" onClick={() => setEditing(v)} testid={`visual-edit-${v.id}`}>EDIT</Btn>
            <button onClick={() => destroy(v)} className="text-red-400/70 hover:text-red-400 text-xs px-1">✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}
